/**
 * Klaviyo Reviews photo carousel — DOM-lift implementation.
 *
 * Reads `.kl_reviews__review__image` photos from the Klaviyo product-
 * reviews widget already on the PDP and clones them into a hoisted
 * carousel slot near the buy box. Hides the carousel cleanly when the
 * widget has no photo reviews.
 *
 * v1 trade-off: this lifts photos from rendered HTML rather than the
 * Klaviyo Reviews API. If Klaviyo changes its widget DOM classes
 * (`kl_reviews__review__image`, `klaviyo-product-reviews-wrapper`),
 * the carousel quietly empties and hides — no broken UI.
 */

(function () {
  'use strict';

  var PHOTO_SELECTOR = '.kl_reviews__review__image';
  var MAX_PHOTOS = 24;
  var POLL_INTERVAL_MS = 250;
  var POLL_TIMEOUT_MS = 8000;
  var SLIDE_WIDTH = 136;

  function liftHighRes(proxiedSrc) {
    if (!proxiedSrc) return proxiedSrc;
    var match = proxiedSrc.match(/\/(?:width|height):\d+(?:\/(?:width|height):\d+)?\/(?:resizing_type:[a-z]+\/)?plain\/(https?:.+)$/);
    if (match) return match[1];
    return proxiedSrc.replace(/(?:width|height):\d+/g, function (m) {
      var label = m.split(':')[0];
      return label + ':1200';
    });
  }

  function collectPhotos(root) {
    var imgs = root.querySelectorAll(PHOTO_SELECTOR);
    var seen = Object.create(null);
    var photos = [];
    for (var i = 0; i < imgs.length && photos.length < MAX_PHOTOS; i++) {
      var img = imgs[i];
      var src = img.getAttribute('src');
      if (!src) continue;
      var key = src.split('?')[0];
      if (seen[key]) continue;
      seen[key] = true;
      photos.push({
        thumb: src,
        full: liftHighRes(src),
        alt: img.getAttribute('alt') || 'Customer photo review',
      });
    }
    return photos;
  }

  function renderSlides(track, photos) {
    var html = '';
    for (var i = 0; i < photos.length; i++) {
      var p = photos[i];
      html += '<li class="kl-photo-carousel__slide">'
        + '<button type="button" class="kl-photo-carousel__thumb-btn" '
        +   'aria-label="Open photo review ' + (i + 1) + '" '
        +   'data-photo-index="' + i + '">'
        + '<img class="kl-photo-carousel__thumb" loading="lazy" '
        +   'width="120" height="120" '
        +   'src="' + p.thumb + '" alt="' + p.alt.replace(/"/g, '&quot;') + '">'
        + '</button>'
        + '</li>';
    }
    track.innerHTML = html;
  }

  function attachCarousel(carousel, photos) {
    var sectionId = carousel.getAttribute('data-section-id');
    var track = carousel.querySelector('[data-photo-carousel-track]');
    var prevBtn = carousel.querySelector('[data-photo-carousel-prev]');
    var nextBtn = carousel.querySelector('[data-photo-carousel-next]');
    var lightbox = document.querySelector(
      '[data-photo-lightbox][data-section-id="' + sectionId + '"]'
    );

    renderSlides(track, photos);
    carousel.style.display = '';

    if (photos.length > 4) {
      if (prevBtn) prevBtn.style.display = '';
      if (nextBtn) nextBtn.style.display = '';
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        track.scrollBy({ left: -(SLIDE_WIDTH * 2), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        track.scrollBy({ left: SLIDE_WIDTH * 2, behavior: 'smooth' });
      });
    }

    var startX = 0;
    track.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
    }, { passive: true });
    track.addEventListener('touchend', function (e) {
      var diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        track.scrollBy({
          left: diff > 0 ? SLIDE_WIDTH * 2 : -(SLIDE_WIDTH * 2),
          behavior: 'smooth',
        });
      }
    }, { passive: true });

    if (!lightbox) return;

    var lightboxImg = lightbox.querySelector('.kl-lightbox__img');
    var currentIndex = 0;

    function openLightbox(index) {
      currentIndex = (index + photos.length) % photos.length;
      var p = photos[currentIndex];
      lightboxImg.src = p.full;
      lightboxImg.alt = p.alt;
      lightbox.style.display = '';
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      lightbox.style.display = 'none';
      document.body.style.overflow = '';
    }

    track.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-photo-index]');
      if (!btn) return;
      openLightbox(parseInt(btn.getAttribute('data-photo-index'), 10));
    });

    lightbox.querySelector('.kl-lightbox__close').addEventListener('click', closeLightbox);
    lightbox.querySelector('.kl-lightbox__prev').addEventListener('click', function () {
      openLightbox(currentIndex - 1);
    });
    lightbox.querySelector('.kl-lightbox__next').addEventListener('click', function () {
      openLightbox(currentIndex + 1);
    });
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
    document.addEventListener('keydown', function (e) {
      if (lightbox.style.display === 'none') return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') openLightbox(currentIndex - 1);
      else if (e.key === 'ArrowRight') openLightbox(currentIndex + 1);
    });
  }

  function populateFromWidget(carousel) {
    var sourceSelector = carousel.getAttribute('data-source-selector');
    if (!sourceSelector) return;
    var deadline = Date.now() + POLL_TIMEOUT_MS;
    var attached = false;

    function tryAttach() {
      if (attached) return true;
      var source = document.querySelector(sourceSelector);
      if (source) {
        var photos = collectPhotos(source);
        if (photos.length > 0) {
          attachCarousel(carousel, photos);
          attached = true;
          return true;
        }
      }
      return false;
    }

    if (tryAttach()) return;

    var poll = setInterval(function () {
      if (tryAttach() || Date.now() > deadline) {
        clearInterval(poll);
      }
    }, POLL_INTERVAL_MS);
  }

  function init() {
    var carousels = document.querySelectorAll('[data-photo-carousel]');
    carousels.forEach(function (carousel) {
      populateFromWidget(carousel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
